
import { Component, Inject, Injectable, Input, NgModule, OnChanges, OnDestroy, SimpleChanges, DOCUMENT, ChangeDetectionStrategy } from '@angular/core';

/**
 * In-house replacement for the unmaintained `ng-keyboard-shortcuts` package (it does not support Angular 22).
 * Keeps the same selector, input and types, and reproduces the library's matching rules for the key
 * combinations used in this app ('ctrl + s', 'esc', 'ctrl + enter', 'ctrl + space'):
 * - modifiers listed in the key must be held; extra modifiers do not prevent a match;
 * - a single key (e.g. 'esc') does not match while any modifier is held;
 * - only one shortcut runs per keydown: the one with the most key tokens, and on a tie the latest registered;
 * - the focus check (allowIn) is applied to that winner only;
 * - preventDefault also stops propagation.
 */
export enum AllowIn {
  Textarea = 'TEXTAREA',
  Input = 'INPUT',
  Select = 'SELECT',
  ContentEditable = 'CONTENT-EDITABLE',
}

export interface ShortcutEventOutput {
  event: KeyboardEvent;
  key: string | string[];
}

export interface ShortcutInput {
  key: string | string[];
  command: (output: ShortcutEventOutput) => any;
  allowIn?: AllowIn[];
  preventDefault?: boolean;
  label?: string;
  description?: string;
}

interface ParsedShortcut {
  id: number;
  input: ShortcutInput;
  keys: string[];
  priority: number;
  predicates: ((event: KeyboardEvent) => boolean)[][];
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);

const MODIFIERS: Record<string, keyof KeyboardEvent> = {
  shift: 'shiftKey',
  ctrl: 'ctrlKey',
  control: 'ctrlKey',
  alt: 'altKey',
  option: 'altKey',
  cmd: isMac ? 'metaKey' : 'ctrlKey',
  meta: isMac ? 'metaKey' : 'ctrlKey',
};

const SPECIAL_KEYS: Record<number, string[]> = {
  8: ['backspace'],
  9: ['tab'],
  13: ['enter'],
  27: ['esc', 'escape'],
  32: ['space', 'spc'],
  46: ['del', 'delete'],
};

const IGNORED: string[] = [AllowIn.Input, AllowIn.Textarea, AllowIn.Select, AllowIn.ContentEditable];

@Injectable({ providedIn: 'root' })
export class KeyboardShortcutsService implements OnDestroy {
  private shortcuts: ParsedShortcut[] = [];
  private nextId = 0;
  private readonly listener = (event: KeyboardEvent) => this.onKeydown(event);

  constructor(@Inject(DOCUMENT) private document: Document) {
    this.document.addEventListener('keydown', this.listener);
  }

  add(inputs: ShortcutInput[]): number[] {
    const parsed = (inputs || []).map(input => this.parse(input));
    this.shortcuts.push(...parsed);
    return parsed.map(p => p.id);
  }

  remove(ids: number[]): void {
    this.shortcuts = this.shortcuts.filter(s => !ids.includes(s.id));
  }

  ngOnDestroy(): void {
    this.document.removeEventListener('keydown', this.listener);
  }

  private parse(input: ShortcutInput): ParsedShortcut {
    const keys = Array.isArray(input.key) ? input.key : [input.key];
    return {
      id: this.nextId++,
      input,
      keys,
      priority: Math.max(...keys.map(k => k.split(' ').filter(Boolean).length)),
      predicates: keys.map(k => this.predicatesFor(k.split(' ').filter(Boolean))),
    };
  }

  private predicatesFor(parts: string[]): ((event: KeyboardEvent) => boolean)[] {
    const tokens = parts.map(p => p.trim()).filter(p => p !== '+');
    return tokens.map(token => {
      const modifier = MODIFIERS[token];
      if (modifier) {
        return (event: KeyboardEvent) => !!event[modifier];
      }
      return (event: KeyboardEvent) => {
        const hasModifiers = Object.values(MODIFIERS).some(m => !!event[m]);
        if (hasModifiers && tokens.length === 1) {
          return false;
        }
        return this.charactersFrom(event).includes(token);
      };
    });
  }

  private charactersFrom(event: KeyboardEvent): string[] {
    const code = event.which || event.keyCode;
    const special = SPECIAL_KEYS[code];
    const chars = special ? [...special] : [String.fromCharCode(code).toLowerCase()];
    return [...chars, event.key];
  }

  private onKeydown(event: KeyboardEvent): void {
    let winner: ParsedShortcut | null = null;
    for (const shortcut of this.shortcuts) {
      const matches = shortcut.predicates.some(all => all.every(predicate => predicate(event)));
      if (matches && (!winner || shortcut.priority >= winner.priority)) {
        winner = shortcut;
      }
    }

    if (!winner || typeof winner.input.command !== 'function' || !this.isAllowed(winner, event)) {
      return;
    }

    if (winner.input.preventDefault) {
      event.preventDefault();
      event.stopPropagation();
    }
    winner.input.command({ event, key: winner.keys });
  }

  private isAllowed(shortcut: ParsedShortcut, event: KeyboardEvent): boolean {
    const target = event.target as HTMLElement;
    if (!target || typeof target.getAttribute !== 'function') {
      return true;
    }
    const isContentEditable = !!target.getAttribute('contenteditable');
    const nodeName = isContentEditable ? AllowIn.ContentEditable : target.nodeName;
    const allowIn = shortcut.input.allowIn || [];
    if (allowIn.length) {
      return !IGNORED.filter(i => !allowIn.includes(i as AllowIn)).includes(nodeName);
    }
    if (isContentEditable) {
      return false;
    }
    return !IGNORED.includes(target.nodeName);
  }
}

@Component({
    selector: 'ng-keyboard-shortcuts',
    template: '',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class KeyboardShortcutsComponent implements OnChanges, OnDestroy {
  @Input() shortcuts: ShortcutInput[] = [];

  private ids: number[] = [];
  private pending: ReturnType<typeof setTimeout> | null = null;

  constructor(private keyboard: KeyboardShortcutsService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['shortcuts']) {
      return;
    }
    this.keyboard.remove(this.ids);
    this.ids = [];
    // Registered after the current change-detection pass, like the library did.
    this.pending = setTimeout(() => {
      this.pending = null;
      this.ids = this.keyboard.add(changes['shortcuts'].currentValue);
    });
  }

  ngOnDestroy(): void {
    if (this.pending) {
      clearTimeout(this.pending);
    }
    this.keyboard.remove(this.ids);
  }
}

@NgModule({
  declarations: [KeyboardShortcutsComponent],
  exports: [KeyboardShortcutsComponent],
})
export class KeyboardShortcutsModule {}
